import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRightIcon,
  CheckCircleIcon,
  HeartIcon,
  UserGroupIcon,
  AcademicCapIcon,
  SparklesIcon,
  TrophyIcon,
  ClockIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const Unterordnung: React.FC = () => {
  const grundkommandos = [
    'Sitz - Der Hund setzt sich auf Kommando',
    'Platz - Der Hund legt sich hin',
    'Fuß - Der Hund läuft an der linken Seite',
    'Hier - Der Hund kommt zum Hundeführer',
    'Bleib - Der Hund verharrt in seiner Position'
  ];

  const fortgeschritteneUebungen = [
    'Präzise Fußarbeit ohne Leine',
    'Distanzkontrolle',
    'Apportieren',
    'Voraussenden',
    'Positionen auf Distanz'
  ];

  const trainingsansatz = [
    {
      icon: UserGroupIcon,
      title: 'Individuell angepasst',
      description: 'Jedes Hund-Mensch-Team ist anders, und wir passen das Training an die Bedürfnisse jedes Einzelnen an.'
    },
    {
      icon: HeartIcon,
      title: 'Positive Verstärkung',
      description: 'Motivation und Lob fördern die Lernbereitschaft und stärken die Bindung im Team.'
    },
    {
      icon: SparklesIcon,
      title: 'Gemeinschaft und Unterstützung',
      description: 'Unser Verein bietet eine freundliche Atmosphäre, in der sich alle wohlfühlen und gegenseitig unterstützen.'
    }
  ];

  const vorteile = [
    'Verbesserte Kommunikation zwischen Mensch und Hund',
    'Sicherheit im Alltag durch zuverlässigen Gehorsam',
    'Mentale Auslastung für Ihren Hund',
    'Stärkung der Bindung und des Vertrauens',
    'Vorbereitung auf weitere Hundesportarten',
    'Sozialverträglichkeit mit anderen Hunden'
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-background-dark">
      <SEO 
        title="Unterordnung & Begleithund"
        description="Unterordnung und Begleithundeprüfung beim HSV Berlin-Adlershof e.V. - Grundgehorsam, Präzision und harmonisches Zusammenspiel zwischen Mensch und Hund."
        url="/unterordnung"
        breadcrumbs={[
          { name: 'Startseite', url: '/' },
          { name: 'Training', url: '/trainings' },
          { name: 'Unterordnung', url: '/unterordnung' }
        ]}
      />
      {/* Hero Section */}
      <section className="relative h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/sports/obedience/companion-dog.jpg" 
            alt="Unterordnung Training" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="container mx-auto px-4 max-w-5xl text-white">
            <h1 className="text-3xl md:text-6xl font-bold mb-3 md:mb-4">
              Unterordnung
            </h1>
            <p className="text-base md:text-2xl opacity-90 max-w-3xl">
              Die Basis für eine starke Mensch-Hund-Beziehung
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="section">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-base md:text-xl text-neutral-600 dark:text-neutral-400 leading-relaxed">
              Unterordnung ist ein zentraler Bestandteil des Hundetrainings und legt die Grundlage für eine 
              harmonische Partnerschaft zwischen Mensch und Hund. Diese Disziplin geht weit über einfachen 
              Gehorsam hinaus und betont das vertrauensvolle Zusammenspiel. Die Übungen in der Unterordnung 
              schulen Aufmerksamkeit, Präzision und Kooperation und schaffen so eine starke Basis für viele 
              andere Hundesportarten.
            </p>
          </div>
        </div>
      </section>

      {/* Was ist Unterordnung */}
      <section className="section bg-neutral-50 dark:bg-background-dark-secondary">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6 text-neutral-900 dark:text-neutral-100">
                Was versteht man unter Unterordnung?
              </h2>
              <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400 mb-4 md:mb-6">
                In der Unterordnung lernt der Hund, aufmerksam und bereitwillig Kommandos seines Hundeführers 
                zu befolgen. Dabei ist das Ziel nicht nur die präzise Ausführung, sondern auch die Freude an 
                der gemeinsamen Arbeit.
              </p>
              <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400">
                Unterordnung fördert den Gehorsam des Hundes und vertieft gleichzeitig das gegenseitige 
                Vertrauen und die Bindung.
              </p>
            </div>
            <div className="relative">
              <img 
                src="/images/sports/obedience/companion-dog.jpg" 
                alt="Training in Aktion"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Die ersten Schritte */}
      <section className="section">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900 dark:text-neutral-100">
              Die ersten Schritte
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
              Unterordnungstraining beginnt mit den Grundkommandos wie „Sitz", „Platz" und „Fuß". 
              Im Training achten wir auf eine ruhige, motivierende Atmosphäre und setzen auf positive 
              Verstärkung, damit der Hund Spaß an den Übungen hat.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 max-w-4xl mx-auto">
            {grundkommandos.map((kommando, index) => (
              <div key={index} className="card p-4 md:p-6 text-center hover:shadow-lg transition-shadow">
                <CheckCircleIcon className="w-8 h-8 text-primary-600 dark:text-primary-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {kommando}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fortgeschrittene Übungen */}
      <section className="section bg-primary-50 dark:bg-primary-900/10">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6 text-neutral-900 dark:text-neutral-100">
                Fortgeschrittene Übungen und Prüfungen
              </h2>
              <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400 mb-4 md:mb-6">
                Nach den Grundübungen kann das Team fortgeschrittenere Aufgaben bewältigen. 
                Dabei arbeiten Mensch und Hund an präziser Fußarbeit, harmonischem Laufen an 
                der Seite und perfektem Reagieren auf Handzeichen und Kommandos.
              </p>
              <div className="p-6 bg-white dark:bg-surface-dark rounded-xl shadow-lg">
                <div className="flex items-center mb-4">
                  <TrophyIcon className="w-8 h-8 text-primary-600 dark:text-primary-400 mr-3" />
                  <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                    Begleithundeprüfung (BHP)
                  </h3>
                </div>
                <p className="text-neutral-600 dark:text-neutral-400">
                  Wer weiterkommen möchte, kann die Begleithundeprüfung ablegen. Diese Prüfung ist 
                  eine Voraussetzung für viele Hundesportarten und zeigt, dass der Hund sich auch 
                  in alltäglichen Situationen gut benimmt.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold mb-4 text-neutral-900 dark:text-neutral-100">
                Fortgeschrittene Übungen:
              </h3>
              {fortgeschritteneUebungen.map((uebung, index) => (
                <div key={index} className="flex items-center p-4 bg-white dark:bg-surface-dark rounded-lg shadow-sm">
                  <CheckCircleIcon className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3 flex-shrink-0" />
                  <span className="text-neutral-700 dark:text-neutral-300">{uebung}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Unser Trainingsansatz */}
      <section className="section">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900 dark:text-neutral-100">
              Unser Trainingsansatz
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {trainingsansatz.map((ansatz, index) => {
              const Icon = ansatz.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-neutral-900 dark:text-neutral-100">
                    {ansatz.title}
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-400">
                    {ansatz.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Warum Unterordnung */}
      <section className="section bg-neutral-50 dark:bg-background-dark-secondary">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-neutral-900 dark:text-neutral-100">
              Warum Unterordnung?
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 text-center mb-12">
              Unterordnung ist nicht nur eine Disziplin, sondern auch ein wichtiger Baustein für eine 
              gute Beziehung und ein harmonisches Zusammenleben. Sie hilft Hund und Halter, besser 
              aufeinander einzugehen und bildet eine solide Grundlage für das Vertrauen im Alltag 
              sowie in weiteren Hundesportarten.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {vorteile.map((vorteil, index) => (
                <div key={index} className="flex items-start p-4 bg-white dark:bg-surface-dark rounded-lg shadow-sm">
                  <CheckCircleIcon className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-700 dark:text-neutral-300">{vorteil}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trainingszeiten */}
      <section className="section bg-primary-600 text-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center">
            <ClockIcon className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              Trainingszeiten für Unterordnung
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <CalendarIcon className="w-8 h-8 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Montag</h3>
                <p className="text-lg">17:00 - 19:00 Uhr</p>
                <p className="text-sm opacity-90">Alle Leistungsstufen</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <CalendarIcon className="w-8 h-8 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Samstag</h3>
                <p className="text-lg">10:00 - 12:00 Uhr</p>
                <p className="text-sm opacity-90">Anfänger & Fortgeschrittene</p>
              </div>
            </div>

            <p className="text-xl mb-8 opacity-95">
              Wir laden Sie herzlich ein, bei einem unserer Trainings vorbeizuschauen und die Freude 
              an der Unterordnung gemeinsam mit Ihrem Hund zu entdecken!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/kontakt" 
                className="inline-flex items-center justify-center bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-neutral-100 transition-colors"
              >
                Auf Warteliste setzen
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </Link>
              <Link 
                to="/trainings" 
                className="inline-flex items-center justify-center bg-transparent text-white border-2 border-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors"
              >
                Alle Sportarten ansehen
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Unterordnung;